import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputBase,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  NotificationsNone as AlertsIcon,
  ShoppingCartOutlined as ReorderIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  CheckCircleOutline as DoneIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import inventoryAlertService from '../../../../services/inventoryAlertService';
import type { AlertSummary, AlertType, InventoryAlert } from '../../../../services/inventoryAlertService';
import { getLinks } from '../../../../services/supplierItemLinkService';
import type { SupplierItemLink } from '../../../../services/supplierItemLinkService';
import purchaseOrderService from '../../../../services/purchaseOrderService';

// ── Alert type config ─────────────────────────────────────────────────────────

const ALERT_CONFIG: Record<AlertType, { label: string; color: string; bg: string }> = {
  NEGATIVE_STOCK:   { label: 'Negative Stock',  color: '#cf222e', bg: '#fff0ee' },
  OUT_OF_STOCK:     { label: 'Out of Stock',     color: '#9a6700', bg: '#fdf6e3' },
  LOW_STOCK:        { label: 'Low Stock',        color: '#0969da', bg: '#f0f6ff' },
  NO_SUPPLIER_LINK: { label: 'No Supplier Link', color: '#6e40c9', bg: '#f8f3ff' },
  NEVER_RECEIVED:   { label: 'Never Received',   color: '#57606a', bg: '#f6f8fa' },
};

const REORDERABLE = new Set<AlertType>(['LOW_STOCK', 'OUT_OF_STOCK', 'NEGATIVE_STOCK']);

const ACTION_LABEL: Record<AlertType, string> = {
  NEGATIVE_STOCK:   'Adjust',
  OUT_OF_STOCK:     'Adjust',
  LOW_STOCK:        'View Item',
  NO_SUPPLIER_LINK: 'Add Supplier',
  NEVER_RECEIVED:   'View Item',
};

// ── Supplier grouping types ───────────────────────────────────────────────────

interface ReorderItem {
  inventoryItemId: string;
  displayName: string | null;
  bottleSize?: number;
  quantity: number;
  reorderSuggestion?: number;
}

interface ReorderGroup {
  supplierName: string;
  items: ReorderItem[];
}

type GroupedBySupplier = Record<string, ReorderGroup>;

/**
 * Pick the best active supplier link.
 * Priority:
 *   1. isPreferred === true
 *   2. Most recently created (createdAt desc)
 *   3. First active link (safety fallback — never null when active links exist)
 */
function resolveSupplierLink(links: SupplierItemLink[]): SupplierItemLink | null {
  const active = links.filter(l => l.active === true);
  if (active.length === 0) return null;

  // 1. Preferred
  const preferred = active.find(l => l.isPreferred === true);
  if (preferred) return preferred;

  // 2. Most recently created
  const sorted = [...active].sort((a, b) => {
    const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return tb - ta;
  });

  // sorted[0] is always defined here since active.length > 0
  return sorted[0];
}

// ── Summary pill ──────────────────────────────────────────────────────────────

interface SummaryPillProps {
  label: string;
  count: number | null;
  color: string;
  loaded: boolean;
}

const SummaryPill: React.FC<SummaryPillProps> = ({ label, count, color, loaded }) => {
  const theme = useTheme();
  const hasAlerts = loaded && count != null && count > 0;
  return (
    <Paper
      sx={{
        px: 2,
        py: 1.5,
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        flex: '1 1 130px',
        minWidth: 115,
        borderLeft: `3px solid ${hasAlerts ? color : theme.palette.divider}`,
        transition: 'border-color 0.2s',
      }}
    >
      <Typography
        variant="caption"
        fontWeight={600}
        sx={{
          color: theme.palette.text.secondary,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          display: 'block',
          mb: 0.5,
          fontSize: '0.65rem',
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="h5"
        fontWeight={700}
        sx={{ color: hasAlerts ? color : theme.palette.text.disabled }}
      >
        {loaded ? (count ?? 0) : '—'}
      </Typography>
    </Paper>
  );
};

// ── Component ─────────────────────────────────────────────────────────────────

const InventoryAlerts: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { venueId } = useVenue();

  const base = `/admin/venues/${venueId}/inventory`;

  const [summary, setSummary] = useState<AlertSummary | null>(null);
  const [alerts, setAlerts] = useState<InventoryAlert[]>([]);
  const [loading, setLoading] = useState(true);

  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // itemId → supplier links, populated once when selection mode activates
  const [linkCache, setLinkCache] = useState<Map<string, SupplierItemLink[]>>(new Map());
  const [linksLoading, setLinksLoading] = useState(false);
  // itemId → user-edited order quantity
  const [editedQuantities, setEditedQuantities] = useState<Record<string, number>>({});
  // supplierId → in-progress flag for PO creation
  const [creatingPO, setCreatingPO] = useState<Record<string, boolean>>({});
  // supplierId → created PO id (marks batch as done, prevents duplicate creation)
  const [createdPOs, setCreatedPOs] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<string | null>(null);

  const reorderableAlerts = alerts.filter(a => REORDERABLE.has(a.type));

  const defaultQty = (alert: InventoryAlert) => alert.reorderSuggestion ?? 0;

  const toggleSelectionMode = () => {
    setSelectionMode(prev => !prev);
    setSelectedIds(new Set());
    setLinkCache(new Map());
    setEditedQuantities({});
    setCreatingPO({});
    setCreatedPOs({});
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
    setLinkCache(new Map());
    setEditedQuantities({});
    setCreatingPO({});
    setCreatedPOs({});
  };

  const toggleRow = (id: string) => {
    const alert = reorderableAlerts.find(a => a.inventoryItemId === id);
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    setEditedQuantities(prev => {
      if (selectedIds.has(id)) {
        // deselecting — remove
        const next = { ...prev };
        delete next[id];
        return next;
      }
      // selecting — add default
      return { ...prev, [id]: alert ? defaultQty(alert) : 0 };
    });
  };

  const setQty = (id: string, value: number) => {
    setEditedQuantities(prev => ({ ...prev, [id]: Math.max(0, value) }));
  };

  const createDraftPO = async (supplierId: string, group: ReorderGroup) => {
    if (!venueId) return;
    const lines = group.items.filter(item => (editedQuantities[item.inventoryItemId] ?? 0) > 0);
    if (lines.length === 0) return;

    setCreatingPO(prev => ({ ...prev, [supplierId]: true }));
    try {
      const po = await purchaseOrderService.createOrder({ supplierId, venueId });
      await Promise.all(
        lines.map(item => {
          const orderedQty = editedQuantities[item.inventoryItemId] ?? item.reorderSuggestion ?? 1;
          return purchaseOrderService.createLine(po._id, {
            inventoryItemId: item.inventoryItemId,
            orderedQty,
          });
        })
      );
      const shortId = po._id.slice(-5).toUpperCase();
      setCreatedPOs(prev => ({ ...prev, [supplierId]: po._id }));
      setToast(`Draft PO created — ${group.supplierName} · #${shortId}`);
    } catch {
      setToast(`Failed to create PO for ${group.supplierName}. Please try again.`);
    } finally {
      setCreatingPO(prev => ({ ...prev, [supplierId]: false }));
    }
  };

  const allReorderableSelected =
    reorderableAlerts.length > 0 &&
    reorderableAlerts.every(a => selectedIds.has(a.inventoryItemId));

  const toggleSelectAll = () => {
    if (allReorderableSelected) {
      setSelectedIds(new Set());
      setEditedQuantities({});
    } else {
      setSelectedIds(new Set(reorderableAlerts.map(a => a.inventoryItemId)));
      const defaults: Record<string, number> = {};
      for (const a of reorderableAlerts) defaults[a.inventoryItemId] = defaultQty(a);
      setEditedQuantities(defaults);
    }
  };

  // Fetch supplier links for all reorderable items when selection mode activates
  useEffect(() => {
    if (!selectionMode || !venueId || reorderableAlerts.length === 0) return;
    setLinksLoading(true);
    Promise.allSettled(
      reorderableAlerts.map(a => getLinks(venueId, a.inventoryItemId).then(links => ({ id: a.inventoryItemId, links })))
    ).then(results => {
      const map = new Map<string, SupplierItemLink[]>();
      for (const r of results) {
        if (r.status === 'fulfilled') map.set(r.value.id, r.value.links);
      }
      setLinkCache(map);
    }).finally(() => setLinksLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionMode, venueId]);

  // Derive supplier grouping from current selection + link cache
  const { groupedBySupplier, unresolvedItems } = useMemo<{
    groupedBySupplier: GroupedBySupplier;
    unresolvedItems: ReorderItem[];
  }>(() => {
    if (selectedIds.size === 0) return { groupedBySupplier: {}, unresolvedItems: [] };

    const groups: GroupedBySupplier = {};
    const unresolved: ReorderItem[] = [];

    for (const alert of reorderableAlerts) {
      if (!selectedIds.has(alert.inventoryItemId)) continue;

      const item: ReorderItem = {
        inventoryItemId: alert.inventoryItemId,
        displayName: alert.displayName,
        bottleSize: alert.bottleSize,
        quantity: alert.quantity,
        reorderSuggestion: alert.reorderSuggestion,
      };

      const links = linkCache.get(alert.inventoryItemId) ?? [];
      const resolved = resolveSupplierLink(links);

      if (!resolved) {
        unresolved.push(item);
      } else {
        const sid = resolved.supplierId;
        if (!groups[sid]) {
          groups[sid] = { supplierName: resolved.supplierName ?? sid, items: [] };
        }
        groups[sid].items.push(item);
      }
    }

    return { groupedBySupplier: groups, unresolvedItems: unresolved };
  }, [selectedIds, linkCache, reorderableAlerts]);

  useEffect(() => {
    if (!venueId) return;
    setLoading(true);
    inventoryAlertService.getAlerts(venueId)
      .then(data => {
        setSummary(data.summary);
        setAlerts(data.alerts);
      })
      .catch(() => { /* fail softly — empty state renders */ })
      .finally(() => setLoading(false));
  }, [venueId]);

  const totalAlerts = alerts.length;
  const loaded = !loading;

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(base)}
          sx={{ textTransform: 'none', color: theme.palette.text.secondary, pl: 0, minWidth: 0 }}
        >
          Inventory
        </Button>
        <Typography variant="body2" color="text.disabled">/</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
          <AlertsIcon sx={{ color: theme.palette.text.secondary, fontSize: 20 }} />
          <Typography variant="h6" fontWeight={600}>Inventory Alerts</Typography>
          {loaded && totalAlerts > 0 && (
            <Typography variant="caption" color="text.secondary">({totalAlerts})</Typography>
          )}
        </Box>
        {loaded && reorderableAlerts.length > 0 && (
          selectionMode ? (
            <Button
              size="small"
              startIcon={<CloseIcon />}
              onClick={exitSelectionMode}
              sx={{ textTransform: 'none', color: theme.palette.text.secondary, fontWeight: 500 }}
            >
              Cancel
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              startIcon={<ReorderIcon />}
              onClick={toggleSelectionMode}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8rem',
                borderRadius: 1.5,
                borderColor: alpha(theme.palette.text.primary, 0.2),
                color: theme.palette.text.secondary,
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                },
              }}
            >
              Reorder
            </Button>
          )
        )}
      </Box>

      {/* Selection info bar */}
      {selectionMode && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: 2,
            py: 1,
            borderRadius: 1.5,
            backgroundColor: alpha(theme.palette.primary.main, 0.06),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
          }}
        >
          <Typography variant="body2" fontWeight={500} color="primary">
            {selectedIds.size > 0
              ? `${selectedIds.size} item${selectedIds.size > 1 ? 's' : ''} selected`
              : 'Select items to reorder'}
          </Typography>
          {reorderableAlerts.length > 0 && (
            <Button
              size="small"
              onClick={toggleSelectAll}
              sx={{ textTransform: 'none', fontSize: '0.78rem', py: 0.25, ml: 'auto' }}
            >
              {allReorderableSelected ? 'Deselect all' : `Select all (${reorderableAlerts.length})`}
            </Button>
          )}
        </Box>
      )}

      {/* Summary pills */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        <SummaryPill label="Negative Stock"   count={summary?.negativeStock   ?? null} color={ALERT_CONFIG.NEGATIVE_STOCK.color}   loaded={loaded} />
        <SummaryPill label="Out of Stock"     count={summary?.outOfStock      ?? null} color={ALERT_CONFIG.OUT_OF_STOCK.color}     loaded={loaded} />
        <SummaryPill label="Low Stock"        count={summary?.lowStock        ?? null} color={ALERT_CONFIG.LOW_STOCK.color}        loaded={loaded} />
        <SummaryPill label="No Supplier Link" count={summary?.missingSupplier ?? null} color={ALERT_CONFIG.NO_SUPPLIER_LINK.color} loaded={loaded} />
        <SummaryPill label="Never Received"   count={summary?.neverReceived   ?? null} color={ALERT_CONFIG.NEVER_RECEIVED.color}   loaded={loaded} />
      </Box>

      {/* Alerts table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {selectionMode && (
                  <TableCell
                    sx={{
                      width: 44,
                      py: 1.25,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  />
                )}
                {['Type', 'Wine', 'Location', 'Qty', 'Suggestion', 'Action'].map(h => (
                  <TableCell
                    key={h}
                    sx={{
                      fontWeight: 500,
                      color: alpha(theme.palette.text.primary, 0.38),
                      fontSize: '0.68rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.07em',
                      whiteSpace: 'nowrap',
                      py: 1.25,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={selectionMode ? 7 : 6}>
                    <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={28} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : alerts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={selectionMode ? 7 : 6}>
                    <Box sx={{ py: 6, textAlign: 'center', color: theme.palette.text.secondary }}>
                      <AlertsIcon sx={{ fontSize: 40, mb: 1, opacity: 0.2 }} />
                      <Typography variant="body2">No alerts. Inventory looks healthy.</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                alerts.map((alert, idx) => {
                  const cfg = ALERT_CONFIG[alert.type];
                  const isReorderable = REORDERABLE.has(alert.type);
                  const isSelected = selectedIds.has(alert.inventoryItemId);
                  return (
                    <TableRow
                      key={`${alert.inventoryItemId}-${idx}`}
                      hover
                      selected={selectionMode && isSelected}
                      onClick={selectionMode && isReorderable ? () => toggleRow(alert.inventoryItemId) : undefined}
                      sx={selectionMode && isReorderable ? { cursor: 'pointer' } : undefined}
                    >

                      {/* Checkbox */}
                      {selectionMode && (
                        <TableCell sx={{ py: 1.25, width: 44, pr: 0 }} onClick={e => e.stopPropagation()}>
                          {isReorderable ? (
                            <Checkbox
                              size="small"
                              checked={isSelected}
                              onChange={() => toggleRow(alert.inventoryItemId)}
                              sx={{ p: 0.5 }}
                            />
                          ) : null}
                        </TableCell>
                      )}

                      {/* Type */}
                      <TableCell sx={{ py: 1.25, width: 150 }}>
                        <Chip
                          label={cfg.label}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.68rem',
                            color: cfg.color,
                            backgroundColor: cfg.bg,
                            border: `1px solid ${alpha(cfg.color, 0.2)}`,
                            height: 20,
                            borderRadius: '4px',
                          }}
                        />
                      </TableCell>

                      {/* Wine + bottle size */}
                      <TableCell sx={{ py: 1.25 }}>
                        <Typography variant="body2" fontWeight={500} sx={{ lineHeight: 1.3 }}>
                          {alert.displayName ?? '—'}
                          {alert.bottleSize != null && (
                            <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400, whiteSpace: 'nowrap' }}>
                              {' · '}{alert.bottleSize} ml
                            </Box>
                          )}
                        </Typography>
                      </TableCell>

                      {/* Location */}
                      <TableCell sx={{ py: 1.25 }}>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {alert.locationName ?? '—'}
                        </Typography>
                      </TableCell>

                      {/* Qty */}
                      <TableCell sx={{ py: 1.25, width: 56 }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ color: alert.quantity < 0 ? '#cf222e' : theme.palette.text.primary }}
                        >
                          {alert.quantity}
                        </Typography>
                      </TableCell>

                      {/* Reorder suggestion */}
                      <TableCell sx={{ py: 1.25, width: 110 }}>
                        {alert.reorderSuggestion != null ? (
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.text.secondary,
                              fontWeight: 500,
                              backgroundColor: alpha(theme.palette.primary.main, 0.06),
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Suggest {alert.reorderSuggestion}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>

                      {/* Action */}
                      <TableCell sx={{ py: 1.25, width: 110, textAlign: 'right' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            if (alert.type === 'NEGATIVE_STOCK' || alert.type === 'OUT_OF_STOCK') {
                              navigate(`${base}/adjust?itemId=${alert.inventoryItemId}`);
                            } else {
                              navigate(`${base}/${alert.inventoryItemId}`);
                            }
                          }}
                          sx={{
                            textTransform: 'none',
                            fontSize: '0.72rem',
                            py: 0.25,
                            px: 1.25,
                            borderRadius: 1.5,
                            fontWeight: 600,
                            borderColor: alpha(theme.palette.text.primary, 0.2),
                            color: theme.palette.text.secondary,
                            '&:hover': {
                              borderColor: theme.palette.primary.main,
                              color: theme.palette.primary.main,
                              backgroundColor: alpha(theme.palette.primary.main, 0.04),
                            },
                          }}
                        >
                          {ACTION_LABEL[alert.type]}
                        </Button>
                      </TableCell>

                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Reorder panel */}
      {selectionMode && selectedIds.size > 0 && (
        <Paper
          sx={{
            borderRadius: 2,
            px: 2.5,
            py: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          }}
        >
          <Typography
            variant="caption"
            fontWeight={600}
            sx={{
              textTransform: 'uppercase',
              letterSpacing: '0.07em',
              color: theme.palette.text.secondary,
              display: 'block',
              mb: 2,
            }}
          >
            Reorder — {selectedIds.size} item{selectedIds.size > 1 ? 's' : ''}
          </Typography>

          {linksLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={14} />
              <Typography variant="body2" color="text.secondary">Resolving suppliers…</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {Object.entries(groupedBySupplier).map(([supplierId, group], groupIdx, arr) => {
                const activeLineCount = group.items.filter(item => (editedQuantities[item.inventoryItemId] ?? 0) > 0).length;
                const groupAllZero = activeLineCount === 0;
                const createdPoId = createdPOs[supplierId] ?? null;
                const isDone = !!createdPoId;
                return (
                  <React.Fragment key={supplierId}>
                    {groupIdx > 0 && <Divider />}
                    <Box
                      sx={{
                        borderRadius: 2,
                        border: `1px solid ${isDone ? alpha('#1a7f37', 0.3) : alpha(theme.palette.divider, 0.5)}`,
                        p: 1.5,
                        backgroundColor: isDone ? alpha('#1a7f37', 0.04) : theme.palette.background.paper,
                        transition: 'border-color 0.2s, background-color 0.2s',
                      }}
                    >
                      {/* Supplier header */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: isDone ? 0.5 : 1.25 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexGrow: 1, minWidth: 0 }}>
                          {isDone && <DoneIcon sx={{ fontSize: 16, color: '#1a7f37', flexShrink: 0 }} />}
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={700} sx={{ color: isDone ? '#1a7f37' : theme.palette.text.primary }} noWrap>
                              {group.supplierName}
                            </Typography>
                            {/* "View PO" lives here — never in the button slot — to avoid button-swap click leaks */}
                            {isDone && createdPoId && (
                              <Typography
                                component="span"
                                variant="caption"
                                onClick={() => navigate(`/admin/venues/${venueId}/purchasing/${createdPoId}`)}
                                sx={{
                                  color: '#1a7f37',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.25,
                                  '&:hover': { textDecoration: 'underline' },
                                }}
                              >
                                View PO <OpenInNewIcon sx={{ fontSize: 11 }} />
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        {/* Single stable button — props mutate through idle → creating → done.
                            Never replaced by a different element, preventing browser mouseup
                            from landing on a newly mounted "View PO" button and auto-navigating. */}
                        <Button
                          size="small"
                          variant="contained"
                          disableElevation
                          type="button"
                          disabled={isDone || groupAllZero || !!creatingPO[supplierId]}
                          onClick={() => createDraftPO(supplierId, group)}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.78rem',
                            borderRadius: 1.5,
                            py: 0.5,
                            px: 1.5,
                            minWidth: 0,
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                            ...(isDone && {
                              backgroundColor: alpha('#1a7f37', 0.12),
                              color: '#1a7f37',
                              '&.Mui-disabled': {
                                backgroundColor: alpha('#1a7f37', 0.12),
                                color: '#1a7f37',
                              },
                            }),
                          }}
                        >
                          {isDone ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <DoneIcon sx={{ fontSize: 14 }} />
                              Draft PO Created
                            </Box>
                          ) : creatingPO[supplierId] ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <CircularProgress size={12} color="inherit" />
                              Creating…
                            </Box>
                          ) : `Create Draft PO (${activeLineCount} item${activeLineCount !== 1 ? 's' : ''})`}
                        </Button>
                      </Box>

                      {/* Zero-qty hint (only when not done) */}
                      {!isDone && groupAllZero && (
                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1, pl: 0.5 }}>
                          Set at least one quantity to create a PO.
                        </Typography>
                      )}

                      {/* Item rows — hidden once PO is created */}
                      {!isDone && <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {group.items.map(item => {
                          const qty = editedQuantities[item.inventoryItemId] ?? 0;
                          return (
                            <Box
                              key={item.inventoryItemId}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                px: 1.25,
                                py: 0.75,
                                borderRadius: 1.5,
                                backgroundColor: alpha(theme.palette.background.default, 0.5),
                                border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                              }}
                            >
                              {/* Name + size */}
                              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={500} noWrap>
                                  {item.displayName ?? '—'}
                                  {item.bottleSize != null && (
                                    <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400, whiteSpace: 'nowrap' }}>
                                      {' · '}{item.bottleSize} ml
                                    </Box>
                                  )}
                                </Typography>
                                <Typography variant="caption" color="text.disabled">
                                  Stock: {item.quantity}
                                </Typography>
                              </Box>

                              {/* Quantity stepper */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                                  borderRadius: 1.5,
                                  overflow: 'hidden',
                                  flexShrink: 0,
                                }}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => setQty(item.inventoryItemId, qty - 1)}
                                  disabled={qty <= 0}
                                  sx={{ borderRadius: 0, px: 0.75, py: 0.5 }}
                                >
                                  <RemoveIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                                <InputBase
                                  value={qty}
                                  onChange={e => {
                                    const v = parseInt(e.target.value, 10);
                                    setQty(item.inventoryItemId, isNaN(v) ? 0 : v);
                                  }}
                                  inputProps={{ min: 0, style: { textAlign: 'center', width: 36, fontSize: '0.82rem', fontWeight: 600, padding: '2px 0' } }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => setQty(item.inventoryItemId, qty + 1)}
                                  sx={{ borderRadius: 0, px: 0.75, py: 0.5 }}
                                >
                                  <AddIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>}
                    </Box>
                  </React.Fragment>
                );
              })}

              {/* Unresolved items */}
              {unresolvedItems.length > 0 && (
                <>
                  {Object.keys(groupedBySupplier).length > 0 && <Divider />}
                  <Box>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ mb: 1, color: theme.palette.text.secondary }}
                    >
                      No supplier
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {unresolvedItems.map(item => (
                        <Box
                          key={item.inventoryItemId}
                          sx={{
                            px: 1.5,
                            py: 0.75,
                            borderRadius: 1.5,
                            backgroundColor: alpha(theme.palette.background.default, 0.4),
                            border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                          }}
                        >
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {item.displayName ?? '—'}
                            {item.bottleSize != null && (
                              <Box component="span" sx={{ color: 'text.disabled', fontWeight: 400 }}>
                                {' · '}{item.bottleSize} ml
                              </Box>
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.disabled">
                            No active supplier link
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          )}
        </Paper>
      )}

      {/* Toast */}
      <Snackbar
        open={toast !== null}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast(null)}
          severity={toast?.startsWith('Failed') ? 'error' : 'success'}
          variant="filled"
          sx={{ fontWeight: 500, fontSize: '0.82rem' }}
        >
          {toast}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default InventoryAlerts;
